import React from 'react'
import { Card, Typography, Divider, Grid, Tooltip, Skeleton } from '@mui/material'
import Info from '@mui/icons-material/Info'
import AddIcon from '@mui/icons-material/Add'
import AdxSwitch from 'components/reusable/switch/AdxSwitch.js'
import { Box } from '@mui/system'
import CategoryFormModal from './CategoryFormModal'
import WarningDeleteModal from 'module/claim/components/prompt-modal/WarningDeleteModal'
import { deleteCategory, putUpdateCategory } from 'services/api/modules/claim/category'
import { useMutation, useQueryClient } from 'react-query'
import AdxButton from 'components/custom/AdxButton'

const CategorySetting = ({ type, title, data, loading, tooltipText }) => {
  // API to update category
  const queryClient = useQueryClient()

  const { mutate } = useMutation(
    ({ id, data }) => putUpdateCategory(id, data),
    {
      onSuccess: async (res) => {
        queryClient.invalidateQueries('adminCategoryData', { exact: true })
        queryClient.invalidateQueries('categoryData', { exact: true })
      },
      onError: async (e) => console.log(e),
    }
  )

  const handleChangeStatus = ({ id, data }) => mutate({ id, data })

  return (
    <Card
      variant="outlined"
      sx={{
        boxSizing: 'border-box',
        borderRadius: '4.5px',
      }}
    >
      <Grid justifyContent={'space-between'} p={2} container>
        <Grid item display="flex" alignItems="center" columnGap={1}>
          <Typography sx={{ fontWeight: 'medium' }} variant="body1" color="light-gray-text.main">
            {title}
          </Typography>
          <Tooltip title={tooltipText} placement="right" arrow>
            <Info
              sx={{
                fontSize: 18,
                color: 'information.main',
              }}
            />
          </Tooltip>
        </Grid>
        <Grid item minWidth={100}>
          <Typography noWrap variant="subtitle1" color={'alternate.main'}>
            {null}
          </Typography>
        </Grid>
      </Grid>

      <Divider variant="full" />

      {loading ? (
        <>
          <Grid
            justifyContent={'space-between'}
            p={2}
            container
            height={60}
            display="flex"
            alignItems="center"
          >
            <Skeleton variant="text" width={200} />
            <Skeleton variant="text" width={80} />
          </Grid>
          <Divider variant="full" />
        </>
      ) : (
        data?.map((item, index) => {
          return (
            <React.Fragment key={index}>
              <Grid
                justifyContent={'space-between'}
                p={2}
                container
                height={{ xs: 80, lg: 60 }}
                display="flex"
                alignItems="center"
              >
                <Grid
                  item
                  display="flex"
                  position={{ xs: 'static', lg: 'relative' }}
                  flexDirection={{ xs: 'column', lg: 'row' }}
                  gap={0.5}
                >
                  <Typography
                    noWrap
                    width={{ xs: 'calc(100vw - 280px)', lg: 'auto' }}
                    sx={{ fontWeight: 'medium', minWidth: '130px' }}
                    variant="subtitle1"
                    color="dark-text.main"
                  >
                    {item.name}
                  </Typography>
                  {item.allocation && (
                    <Typography
                      noWrap
                      sx={{ fontWeight: 'medium' }}
                      variant="subtitle1"
                      color="light-gray-text.main"
                      position={{ xs: 'static', lg: 'absolute' }}
                      left="420px"
                    >
                      Allocation : RM {parseFloat(item.allocation).toFixed(2)} /{' '}
                      {item.cycle_period === 2 ? 'monthly' : 'annually'}
                    </Typography>
                  )}
                </Grid>
                <Grid item minWidth={100} position="relative">
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      position: 'absolute',
                      top: -23,
                      right: -10,
                      justifyContent: 'flex-end',
                    }}
                  >
                    <CategoryFormModal
                      categoryId={item.id}
                      categoryName={item.name}
                      mileageRate={item.rate}
                      allocation={item.allocation}
                      cyclePeriod={item.cycle_period}
                      type={type}
                    >
                      <Typography
                        mr={2}
                        noWrap
                        variant="subtitle1"
                        color={'alternate.main'}
                        sx={{
                          textDecoration: 'underline',
                          fontWeight: 'medium',
                          cursor: 'pointer',
                        }}
                      >
                        Edit
                      </Typography>
                    </CategoryFormModal>

                    <WarningDeleteModal
                      id={item.id}
                      reactQueryKeys={['adminCategoryData']}
                      deleteAPI={() => deleteCategory(item.id)}
                      title={`Delete ${item.name}?`}
                      subtitle="You will lose all data under the category."
                    >
                      <Typography
                        mr={1}
                        noWrap
                        variant="subtitle1"
                        color={'error.main'}
                        sx={{
                          textDecoration: 'underline',
                          fontWeight: 'medium',
                          cursor: 'pointer',
                        }}
                      >
                        Delete
                      </Typography>
                    </WarningDeleteModal>

                    <AdxSwitch
                      state={item.active}
                      onClick={() =>
                        handleChangeStatus({
                          id: item?.id,
                          data: {
                            name: item?.name,
                            active: !item?.active,
                            cycle_period: item?.cycle_period,
                            rate: item?.rate,
                          },
                        })
                      }
                      sx={{ cursor: 'pointer' }}
                    />
                  </Box>
                </Grid>
              </Grid>
              <Divider variant="full" />
            </React.Fragment>
          )
        })
      )}
      <CategoryFormModal newCategory type={type}>
        <Box p={0.5}>
          <AdxButton
            variant={'ghost'}
            disabled={loading}
            sx={{
              pl: 2,
              display: 'flex',
              justifyContent: 'flex-start',
              width: '100%',
            }}
          >
            <Typography
              display="flex"
              alignItems="center"
              columnGap={0.55}
              color={loading ? 'gray-inactive-button.main' : 'alternate.main'}
              sx={{ padding: 0, fontWeight: 'medium' }}
              variant="subtitle1"
            >
              <AddIcon sx={{ fontSize: 20 }} />
              <Typography component="span">Manage new</Typography>
              <Typography component="span" textTransform="lowercase">
                {title}
              </Typography>
            </Typography>
          </AdxButton>
        </Box>
      </CategoryFormModal>
    </Card>
  )
}

export default CategorySetting
